import { Box, Grid, Typography, useMediaQuery } from '@mui/material';
import ReportDisclaimer from './ReportDisclaimer';
import { LICENSE_NUM } from '../constants/report';
import { useThemeContext } from '../contexts/BaseThemeContextProvider';
import { formatReportDate } from '../utils/dateTime';

type ReportDetailsInfoProps = {
  reportData: {
    org_address: string;
    signing_doctor: Record<string, string>[];
    age: string;
    sample_date: string;
    accession_date: string;
    report_date: string;
    patient_name: string;
    sample_id: string;
    centre_report_id: number;
    test_name: string;
    gender: string;
  };
  isResultDetailsExpanded: boolean;
};

const fieldContainerStyles = {
  display: 'flex',
  flexDirection: { xs: 'row', md: 'column' },
  alignItems: { xs: 'center', md: 'flex-start' },
  gap: '4px',
};

export default function ReportDetailsInfo({ reportData, isResultDetailsExpanded }: ReportDetailsInfoProps) {
  const { theme } = useThemeContext();
  const isSmallDevice = useMediaQuery(theme.breakpoints.down('md'));

  const reportFieldNameStyles = {
    fontSize: '12px',
    color: theme.custom.report.details.infoSection.boxWrapper.fieldName.color,
    fontWeight: 400,
    minWidth: '80px',
  };

  const reportFieldValueStyles = {
    fontSize: '12px',
    color: theme.custom.report.details.infoSection.boxWrapper.fieldValue.color,
    fontWeight: 400,
    wordBreak: 'break-word',
  };

  const gender = reportData.gender[0].toUpperCase() + reportData.gender.slice(1).toLowerCase();

  const renderReportDetails = () => {
    if (isSmallDevice && !isResultDetailsExpanded) return <></>;
    return (
      <Box
        mt={3}
        sx={{
          background: theme.custom.report.details.infoSection.boxWrapper.background,
          marginBottom: '4px',
        }}
      >
        <Grid container spacing={{ xs: 1, md: 0.5 }}>
          <Grid item xs={6} md={1.9}>
            <Box sx={fieldContainerStyles}>
              <Typography variant="body2" sx={reportFieldNameStyles}>
                Patient name:
              </Typography>
              <Typography variant="body2" sx={reportFieldValueStyles}>
                {reportData.patient_name}
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={6} md={1.9}>
            <Box sx={fieldContainerStyles}>
              <Typography variant="body2" sx={reportFieldNameStyles}>
                Sample ID:
              </Typography>
              <Typography variant="body2" sx={reportFieldValueStyles}>
                {reportData.sample_id}
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={6} md={1.7}>
            <Box sx={fieldContainerStyles}>
              <Typography variant="body2" sx={reportFieldNameStyles}>
                Age (gender):
              </Typography>
              <Typography variant="body2" sx={reportFieldValueStyles}>
                {reportData.age} {`(${gender})`}
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={6} md={1.8}>
            <Box sx={fieldContainerStyles}>
              <Typography variant="body2" sx={reportFieldNameStyles}>
                Sample date:
              </Typography>
              <Typography variant="body2" sx={reportFieldValueStyles}>
                {formatReportDate(reportData.sample_date)}
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={6} md={2.8}>
            <Box sx={{ ...fieldContainerStyles, flexDirection: 'column', alignItems: 'flex-start' }}>
              <Typography variant="body2" sx={reportFieldNameStyles}>
                Received:
              </Typography>
              <Typography variant="body2" sx={reportFieldValueStyles}>
                {formatReportDate(reportData.accession_date, true)}
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={6} md={1.9}>
            <Box sx={fieldContainerStyles}>
              <Typography variant="body2" sx={reportFieldNameStyles}>
                Report date:
              </Typography>
              <Typography variant="body2" sx={reportFieldValueStyles}>
                {formatReportDate(reportData.report_date)}
              </Typography>
            </Box>
          </Grid>
        </Grid>

        <Box sx={{ ...fieldContainerStyles, mt: 2 }}>
          <Typography variant="body2" sx={reportFieldNameStyles}>
            License no.:
          </Typography>
          <Typography variant="body2" sx={reportFieldValueStyles}>
            {LICENSE_NUM}
          </Typography>
        </Box>

        <Box sx={{ ...fieldContainerStyles, mt: { xs: 0, md: 1 } }}>
          <Typography variant="body2" sx={reportFieldNameStyles}>
            Lab Address:
          </Typography>
          <Typography variant="body2" sx={reportFieldValueStyles}>
            {reportData.org_address}
          </Typography>
        </Box>
      </Box>
    );
  };

  return (
    <>
      {renderReportDetails()}
      <ReportDisclaimer />
    </>
  );
}
